import React, { useEffect, useState } from 'react';
import { Box, Paper, Container, Fade } from '@mui/material';
import { GridColDef, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Table from '../../../components/table/Table';
import TableHeaderTitle from '../../../components/TableHeaderTitle';
import CustomBtn from '../../../components/customBtn/CustomBtn';
import { filterOperatorsDateTime } from '../../../utils/filter-date-time';
import { useFetchDataQuery } from '../services/services';
import MealAllowanceDetailModal from './components/MealAllowanceDetailModal';
import { useMultipleSendStatusLookupQuery } from '../../../components/services/services';

interface MealAllowanceData {
    batchId: string;
    companyId: number;
    scheduledAt: string;
    relevantName: string;
    status: string;
}

const MealAllowancePage: React.FC = () => {
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
    const [detailModal, setDetailModal] = useState({
        open: false,
        batchId: '',
        status: '',
    });
    const { data: multipleSendStatusData } = useMultipleSendStatusLookupQuery();
    const [requestBody, setRequestBody] = useState({
        filters: [],
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
        customerId: currentUserState?.companyId || undefined,
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });

    const customBtnClick = (params: any) => {
        openDetailControl(params.row);
    };

    const initialColumns: GridColDef[] = [
        {
            field: 'scheduledAt',
            headerName: 'Bakiye Talimat Tarihi',
            width: 200,
            // @ts-ignore
            type: "datetime",
            filterOperators: filterOperatorsDateTime,
            valueFormatter: (params: any) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toLocaleDateString('tr-TR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            },
        },
        {
            field: 'relevantName',
            headerName: 'Talimatı Oluşturan Kişi',
            width: 250,
        },
        {
            field: 'status',
            headerName: 'Bakiye Yüklendi mi?',
            width: 180,
            type: 'singleSelect', 
            valueOptions: []
        },
        {
            field: "detail",
            headerName: 'Detaylar',
            width: 100,
            renderCell: (params) => <CustomBtn name="Detay" onclick={() => customBtnClick(params)} />,
            filterable: false,
            sortable: false
        },
    ];

    useEffect(() => {
        const updatedColumns = initialColumns?.map(column => {
            if (column.field === 'status' && (multipleSendStatusData?.data?.length ?? 0) > 0) {
                return {
                    ...column,
                    valueOptions: multipleSendStatusData?.data?.map((item: any) => ({
                        value: item.value.toString(),
                        label: item.text,
                    })),
                };
            }

            return column;
        });
        setColumns([...updatedColumns]);
    }, [multipleSendStatusData]);

    const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
    const listUrl = `/Process/GetSendMealAllowanceListStatusV2`;

    // API çağrısı
    const { data: fetchData, isFetching, error: fetchError, isLoading: isFetchLoading } = useFetchDataQuery({
        url: listUrl,
        filter: requestBody,
        method: "POST"
    }, {
        skip: !requestBody?.customerId
    });

    // Kurum değişince requestBody.customerId güncellenmezse liste refetch olmayabilir
    useEffect(() => {
        if (!currentUserState?.companyId) return;
        setPaginationModel({ page: 0, pageSize: 25 });
        setRequestBody((prev) => ({
            ...prev,
            filters: [],
            pageSize: 25,
            page: 1,
            orderBy: "",
            orderDesc: false,
            customerId: currentUserState.companyId,
        }));
    }, [currentUserState?.companyId]);

    const debounce = (func: any, delay: any) => {
        let timeoutId: any;
        return (...args: any) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(null, args);
            }, delay);
        };
    };

    const onFilterChange = debounce((filterObj: any) => {
        setRequestBody((prev) => ({ ...prev, filters: filterObj }));
    }, 500);

    const handlePaginationModelChange = (model: any) => {
        setPaginationModel(model);
        setRequestBody((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page + 1 }));
    };

    const handleSortModelChange = (model: any) => {
        setRequestBody((prev) => ({ ...prev, orderBy: model[0]?.field ?? "", orderDesc: model[0]?.sort === "asc" }));
    };

    const openDetailControl = (rowData: MealAllowanceData) => {
        setDetailModal({
            open: true,
            batchId: rowData.batchId,
            status: rowData.status,
        });
    };

    const closeDetailModal = () => {
        setDetailModal({
            open: false,
            batchId: '',
            status: '',
        });
    };

    return (
        <Container maxWidth={false} sx={{
            flexGrow: 1,
            padding: { xs: '110px 8px 0 8px', sm: '110px 12px 0 12px', md: '110px 32px 0 32px', lg: '110px 40px 0 40px' },
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            minHeight: '100vh'
        }}>
            {/* Main Dashboard Container */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 25%, #3b82f6 50%, #2a5298 75%, #1e3c72 100%)',
                    }
                }}
            >
                <Box sx={{
                    py: { xs: 2, sm: 3, md: 4 },
                    pb: 2,
                    px: 0,
                    background: 'linear-gradient(135deg, rgba(30, 60, 114, 0.03) 0%, rgba(42, 82, 152, 0.06) 100%)',
                    borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
                }}>
                    <Fade in={true} timeout={600}>
                        <Box sx={{ px: { xs: 2, sm: 3 }, mx: { xs: 0, sm: 2 } }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.7)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(226, 232, 240, 0.8)',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                            >
                                {/* Table Header */}
                                <Box sx={{
                                    p: 3,
                                    pb: 2,
                                    background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.9) 100%)',
                                    borderBottom: '1px solid rgba(226, 232, 240, 0.6)'
                                }}>
                                    <TableHeaderTitle title="Bakiye Yükleme Talimatları" />
                                </Box>

                                {/* Table Content */}
                                <Box sx={{ p: 3 }}>
                                    <Table
                                        rows={fetchData?.data || []}
                                        columns={columns}
                                        getRowId={(row) => row.batchId}
                                        paginationModel={paginationModel}
                                        onPaginationModelChange={handlePaginationModelChange}
                                        sortModelChange={handleSortModelChange}
                                        headerFilters={true}
                                        checkboxSelection={true}
                                        columnVisibilityConfigKey="mealAllowanceColumnVisibilityKey"
                                        filterKey="mealAllowanceFilter"
                                        handleFilterChange={onFilterChange}
                                        setColumnVisibilityModel={setColumnVisibilityModel}
                                        columnVisibilityModel={columnVisibilityModel}
                                        idName="batchId"
                                        listUrl={listUrl}
                                        fetchData={fetchData}
                                        isLoading={isFetching || (fetchData === undefined && !!requestBody?.customerId)}
                                        rowCount={fetchData?.totalCount || 0}
                                        requestBody={requestBody}
                                        fetchMethod="POST"
                                        initialState={{
                                            pinnedColumns: {
                                                left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'actions'],
                                                right: ['detail'],
                                            },
                                        } as GridInitialStatePro}
                                        sx={{
                                            margin: fetchData?.data?.length > 0 && paginationModel.pageSize > 25 ? "0px 0 25px 0 !important" : "0px !important",
                                            '& .MuiDataGrid-root': {
                                                borderRadius: 2,
                                                border: 'none',
                                            },
                                            '& .MuiDataGrid-main': {
                                                borderRadius: 2,
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)',
                                                borderBottom: '2px solid rgba(226, 232, 240, 0.8)',
                                                borderRadius: '8px 8px 0 0',
                                            },
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
                                            },
                                            '& .MuiDataGrid-row': {
                                                '&:hover': {
                                                    background: 'rgba(59, 130, 246, 0.04)',
                                                },
                                                '&.Mui-selected': {
                                                    background: 'rgba(30, 60, 114, 0.08)',
                                                    '&:hover': {
                                                        background: 'rgba(30, 60, 114, 0.12)',
                                                    },
                                                },
                                            },
                                        }}
                                        showDownloadButton={true}
                                        listName="Bakiye Yükleme Talimatları Listesi"
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </Fade>
                </Box>
            </Paper>

            {/* Detail Modal */}
            <MealAllowanceDetailModal
                open={detailModal.open}
                onClose={closeDetailModal}
                batchId={detailModal.batchId}
                status={detailModal.status}
            />
        </Container>
    );
};

export default MealAllowancePage;
